import { ProductEndpoints } from './endpoints';
import { Product, ProductRequest, ProductUpdateRequest } from '../models';
import * as schemas from './schemas';
import { getToken } from '~/apps/shared/services/token';
import * as net from '~/lib/net';
import { ProductListParams } from './params';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';


export async function getProducts(productListParams: ProductListParams): Promise<Product[]> {
  const data = await net.apiGet(ProductEndpoints.getProductList(productListParams), getToken());
  return schemas.ProductListSchema.load(data);
}

export async function getProducById(productId: string): Promise<Product> {
  const data = await net.apiGet(ProductEndpoints.getProductById(productId), getToken());
  return schemas.ProductSchema.load(data);
}

export async function getOrgProducts(orgId: number, offset = 0, limit = 0): Promise<ProductRequest[]> {
  const data = await net.apiGet(ProductEndpoints.getOrgProducts(orgId, offset, limit), getToken());
  return schemas.ProductListSchema.load(data);
}

export async function getLocationProducts(locationId: string, offset = 0, limit = 0): Promise<Product[]> {
  const data = await net.apiGet(ProductEndpoints.getLocationProducts(locationId, offset, limit), getToken());
  return schemas.ProductListSchema.load(data);
}

// Management
export async function createProduct(req: ProductRequest) {
  const payload = schemas.ProductRequest.save(req);

  const data = await net.apiPost(ProductEndpoints.createProduct(), getToken(), payload);
  return schemas.ProductSchema.load(data);
}

export async function updateProduct(req: ProductUpdateRequest) {
  const { id, ...rest } = req;
  const payload = schemas.ProductRequest.save(rest);
  const data = await net.apiPut(ProductEndpoints.updateProduct(id), getToken(), payload);
  return schemas.ProductSchema.load(data);
}

export const deleteProduct = async (id: string) => {
  return await net.apiDelete(`/api/v2/products/${id}`, getToken());
};


// Queries
export const useLocationProducts = (locationId: string) => {
  return useQuery({
    queryKey: ["products", "list", locationId],
    queryFn: () => getLocationProducts(locationId),
    enabled: !!locationId,
    initialData: [],
  });
};

export const useProductById = (id: string) => {
  return useQuery({
    queryKey: ["products", id],
    queryFn: () => getProducById(id),
    enabled: !!id,
  });
};

export const useCreateProduct = () => {
  const queryClient = useQueryClient();
  return useMutation<Product, { message?: string; errorMessage?: string }, ProductRequest>({
    mutationFn: createProduct,
    onSuccess: () => {
      queryClient.invalidateQueries(["products"]);
    },
  });
};

export const useEditProduct = () => {
  const queryClient = useQueryClient();
  return useMutation<Product, { message?: string; errorMessage?: string }, ProductUpdateRequest>({
    mutationFn: updateProduct,
    onSuccess: () => {
      queryClient.invalidateQueries(["products"]);
    },
  });
};

export const useDeleteProduct = () => {
  const queryClient = useQueryClient();
  return useMutation<undefined, { message?: string; errorMessage?: string }, string>({
    mutationFn: deleteProduct,
    onSuccess: () => {
      queryClient.invalidateQueries(["products"]);
    },
  });
};
